import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import '../../styles/employee/address-info.css';
import TopNavbar from '../top-navbar/TopNav';
import { toast } from 'react-toastify';

const AddressInfo = ({ candidateId , addressInfoResponse, onBack }) => {
    const token = localStorage.getItem('bgc_token');

    const [currentAddress, setCurrentAddress] = useState({
        line1: '',
        line2: '',
        country: '',
        state: '',
        city: '',
        pincode: '',
        proof: null,
    });

    const [permanentAddress, setPermanentAddress] = useState({
        line1: '',
        line2: '',
        country: '',
        state: '',
        city: '',
        pincode: '',
        proof: null,
    });

    const [remarks, setRemarks] = useState('');
    const [verificationStatus, setVerificationStatus] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

     useEffect(() => {
        const fetchAddressData = async () => {
            if (!candidateId || !token) {
                setFetchingData(false);
                return;
            }

            try {
                const response = await fetch(
                    `https://trueface.co.in/api/employee/address/get-address-info/${candidateId}`,
                    {
                        method: 'GET',
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch address data.');
                }

                const result = await response.json();
                const data = result?.data;

                if (data) {
                    // Populate from backend response
                    setCurrentAddress({
                        line1: data.currentAddressLine1 || '',
                        line2: data.currentAddressLine2 || '',
                        country: data.currentCountry || '',
                        state: data.currentState || '',
                        city: data.currentCity || '',
                        pincode: data.currentPinCode || '',
                        proof: data.currentAddressVerificationUrl || null,
                    });

                    setPermanentAddress({
                        line1: data.permanentAddressLine1 || '',
                        line2: data.permanentAddressLine2 || '',
                        country: data.permanentCountry || '',
                        state: data.permanentState || '',
                        city: data.permanentCity || '',
                        pincode: data.permanentPinCode || '',
                        proof: data.permanentAddressVerificationUrl || null,
                    });

                    setRemarks(data.remarks || '');
                    setVerificationStatus(data.verificationStatus || false);
                } else {
                    // Fallback to props
                    setCurrentAddress({
                        line1: addressInfoResponse?.currentAddressLine1 || '',
                        line2: addressInfoResponse?.currentAddressLine2 || '',
                        country: addressInfoResponse?.currentCountry || '',
                        state: addressInfoResponse?.currentState || '',
                        city: addressInfoResponse?.currentCity || '',
                        pincode: addressInfoResponse?.currentPinCode || '',
                        proof: addressInfoResponse?.currentAddressProofFileName || null,
                    });

                    setPermanentAddress({
                        line1: addressInfoResponse?.permanentAddressLine1 || '',
                        line2: addressInfoResponse?.permanentAddressLine2 || '',
                        country: addressInfoResponse?.permanentCountry || '',
                        state: addressInfoResponse?.permanentState || '',
                        city: addressInfoResponse?.permanentCity || '',
                        pincode: addressInfoResponse?.permanentPinCode || '',
                        proof: addressInfoResponse?.permanentAddressProofFileName || null,
                    });
                }
            } catch (err) {
                console.error('Error fetching address data:', err);
                setError(err.message);

                // Fallback on error
                setCurrentAddress({
                    line1: addressInfoResponse?.currentAddressLine1 || '',
                    line2: addressInfoResponse?.currentAddressLine2 || '',
                    country: addressInfoResponse?.currentCountry || '',
                    state: addressInfoResponse?.currentState || '',
                    city: addressInfoResponse?.currentCity || '',
                    pincode: addressInfoResponse?.currentPinCode || '',
                    proof: addressInfoResponse?.currentAddressProofFileName || null,
                });

                setPermanentAddress({
                    line1: addressInfoResponse?.permanentAddressLine1 || '',
                    line2: addressInfoResponse?.permanentAddressLine2 || '',
                    country: addressInfoResponse?.permanentCountry || '',
                    state: addressInfoResponse?.permanentState || '',
                    city: addressInfoResponse?.permanentCity || '',
                    pincode: addressInfoResponse?.permanentPinCode || '',
                    proof: addressInfoResponse?.permanentAddressProofFileName || null,
                });
            } finally {
                setFetchingData(false);
            }
        };

        fetchAddressData();
    }, [candidateId, token, addressInfoResponse]);

    const handleFileChange = (e, addressType) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];

            // Allowed file types
            const allowedFileTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

            // Check if the file type is allowed
            if (!allowedFileTypes.includes(file.type)) {
                toast.error("Please upload a file in JPG, JPEG, PNG, or PDF format.");
                return;
            }

            // Update the state if the file type is valid
            if (addressType === 'current') {
                setCurrentAddress({ ...currentAddress, proof: file });
            } else {
                setPermanentAddress({ ...permanentAddress, proof: file });
            }
        }
    };

    const getFileNameFromUrl = (url) => {
  return url ? url.split('/').pop() : "";
};

    const handleVerificationStatusChange = (e) => {
        setVerificationStatus(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

       

    const hasCurrentProof = currentAddress.proof instanceof File || (typeof currentAddress.proof === "string" && currentAddress.proof);
    const hasPermanentProof = permanentAddress.proof instanceof File || (typeof permanentAddress.proof === "string" && permanentAddress.proof);

    if (!hasCurrentProof && !hasPermanentProof) {
        toast.error("At least one address proof (Current or Permanent) is required.");
        setLoading(false);
        return;
    }

    if (!remarks.trim()) {
            toast.error("Remarks are required.");
            setLoading(false);
            return;
     }

        const formData = new FormData();

        if (!candidateId) {
            setError("Candidate ID is missing. Cannot submit.");
            setLoading(false);
            return;
        }

        formData.append('candidateId', candidateId);
        formData.append('currentAddressLine1', currentAddress.line1);
        formData.append('currentAddressLine2', currentAddress.line2);
        formData.append('currentCountry', currentAddress.country);
        formData.append('currentState', currentAddress.state);
        formData.append('currentCity', currentAddress.city);
        formData.append('currentPinCode', currentAddress.pincode);

        formData.append('permanentAddressLine1', permanentAddress.line1);
        formData.append('permanentAddressLine2', permanentAddress.line2);
        formData.append('permanentCountry', permanentAddress.country);
        formData.append('permanentState', permanentAddress.state);
        formData.append('permanentCity', permanentAddress.city);
        formData.append('permanentPinCode', permanentAddress.pincode);

        formData.append('remarks', remarks);
        formData.append('verificationStatus', JSON.stringify(verificationStatus));

        if (currentAddress.proof instanceof File) {
            formData.append('currentAddressProofFile', currentAddress.proof);
        }
        if (permanentAddress.proof instanceof File) {
            formData.append('permanentAddressProofFile', permanentAddress.proof);
        }

        if (!token) {
            setError("Authentication token not found. Please log in.");
            setLoading(false);
            return;
        }

        try {
            const response = await fetch('https://trueface.co.in/api/employee/address/verify', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to verify address.');
            }

            const result = await response.json();
            toast.success("Address Check data saved successfully", {
                            autoClose: 3000, // 3 seconds
                            });
            //console.log('Verification successful:', result);
            onBack();
        } catch (err) {
            console.error('Error during address verification:', err);
            toast.error(err.message || 'An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="dashboard-app-container">
            <Sidebar />
            <div className="dashboard-right-panel">
                <TopNavbar />
                    <div className="address-info-header">
                        <h2>Address Status</h2>
                    </div>
                    
                <div className="address-info-container">
                
                    <form onSubmit={handleSubmit} className="address-info-form">
                        {/* Current Address Section */}
                        <h3>Current Address</h3>
                        <div className="address-section">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label><strong>Address Line 1:</strong></label>
                                    <textarea value={currentAddress.line1} disabled rows="3" />
                                </div>
                                <div className="form-group">
                                    <label><strong>Address Line 2:</strong></label>
                                    <textarea value={currentAddress.line2} disabled rows="3" />
                                </div>
                                <div className="form-group">
                                    <label><strong>Country:</strong></label>
                                    <input type="text" value={currentAddress.country} disabled />
                                </div>
                                <div className="form-group">
                                    <label><strong>State:</strong></label>
                                    <input type="text" value={currentAddress.state} disabled />
                                </div>
                                <div className="form-group">
                                    <label><strong>City:</strong></label>
                                    <input type="text" value={currentAddress.city} disabled />
                                </div>
                                <div className="form-group">
                                    <label><strong>PinCode:</strong></label>
                                    <input type="text" value={currentAddress.pincode} disabled />
                                </div>
                            </div>

                            <div className="form-group file-upload-group">
                                <label><strong>Address Proof:</strong></label>
                                <div className="file-name-wrapper">
                                    <span className="file-name">
                                        {currentAddress.proof instanceof File ? currentAddress.proof.name : getFileNameFromUrl(currentAddress.proof) || 'No file chosen'}
                                    </span>
                                </div>
                                <div className="file-input-wrapper">
                                    <input
                                        type="file"
                                        id="currentAddressProof"
                                        className="file-input"
                                        onChange={(e) => handleFileChange(e, 'current')}
                                    />
                                    <label htmlFor="currentAddressProof" className="choose-file-button">
                                        Choose File
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Permanent Address Section */}
                        <h3>Permanent Address</h3>
                        <div className="address-section">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label><strong>Address Line 1:</strong></label>
                                    <textarea value={permanentAddress.line1} disabled rows="3" />
                                </div>
                                <div className="form-group">
                                    <label><strong>Address Line 2:</strong></label>
                                    <textarea value={permanentAddress.line2} disabled rows="3" />
                                </div>
                                <div className="form-group">
                                    <label><strong>Country:</strong></label>
                                    <input type="text" value={permanentAddress.country} disabled />
                                </div>
                                <div className="form-group">
                                    <label><strong>State:</strong></label>
                                    <input type="text" value={permanentAddress.state} disabled />
                                </div>
                                <div className="form-group">
                                    <label><strong>City:</strong></label>
                                    <input type="text" value={permanentAddress.city} disabled />
                                </div>
                                <div className="form-group">
                                    <label><strong>PinCode:</strong></label>
                                    <input type="text" value={permanentAddress.pincode} disabled />
                                </div>
                            </div>

                            <div className="form-group file-upload-group">
                                <label><strong>Address Proof:</strong></label>
                                <div className="file-name-wrapper">
                                    <span className="file-name">
                                        {permanentAddress.proof instanceof File ? permanentAddress.proof.name : getFileNameFromUrl(permanentAddress.proof) || 'No file chosen'}
                                    </span>
                                </div>
                                <div className="file-input-wrapper">
                                    <input
                                        type="file"
                                        id="permanentAddressProof"
                                        className="file-input"
                                        onChange={(e) => handleFileChange(e, 'permanent')}
                                    />
                                    <label htmlFor="permanentAddressProof" className="choose-file-button">
                                        Choose File
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="remarks-section">
                            <label><strong>Remarks:</strong></label>
                            <textarea
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                placeholder="Type here..."
                            />
                        </div>

                      <div className="checkbox-group">
                        <label>
                            <input
                                type="checkbox"
                                name="verificationStatus"
                                checked={verificationStatus}
                                onChange={() => setVerificationStatus(!verificationStatus)}
                            />{" "}
                            Verification Status
                        </label>
                        <p className="verification-note">
                            ⚠️ Once verified, this step will be locked and you cannot return to modify the address details.
                        </p>
                    </div>


                        {/* {loading && <p className="status-message">Submitting...</p>}
                        {error && <p className="error-message">{error}</p>}
                        {success && <p className="success-message">Address verification submitted successfully!</p>} */}

                        <div className="form-actions">
                            <button type="button" className="cancel-button" onClick={onBack} disabled={loading}>Cancel</button>
                            <button type="submit" className="submit-button" disabled={loading}>
                                {loading ? 'Submitting...' : 'Submit'} →
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

AddressInfo.propTypes = {
    candidateId: PropTypes.object,
    addressInfoResponse: PropTypes.object.isRequired,
    onBack: PropTypes.func.isRequired,
};

export default AddressInfo;
